<template>
  <modal :show.sync="showModal" header-classes="justify-content-center" @close="handleClose">
    <h4 slot="header" class="title title-up">
      {{ editingData ? 'Edit High Call' : 'Log a High Call' }}
    </h4>
    <el-row :gutter="20">
      <el-col :span="8">
        <label class="col-form-label top-margin">Select Presenter:</label>
      </el-col>
      <el-col :span="16">
        <el-select
          class="col select-default"
          filterable
          clearable
          v-model="newDisciplerPersonId"
          placeholder="Select Presenter..."
        >
          <el-option
            v-for="item in addableDisciplers"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-col>
      <span v-if="validationError.discipler" class="text-danger text-sm">
        Please select a presenter.
      </span>
    </el-row>

    <el-row :gutter="20" v-if="!editingData">
      <el-col :span="8">
        <label class="col-form-label top-margin">Select Receiver:</label>
      </el-col>
      <el-col :span="16">
        <el-select
          class="col select-default"
          filterable
          clearable
          v-model="newDisciplePersonId"
          placeholder="Select Receiver..."
        >
          <el-option
            v-for="item in possibleRecipients"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-col>
      <span v-if="validationError.disciple" class="text-danger text-sm">
        Please select a receiver.
      </span>
    </el-row>

    <hr class="divider" />

    <!-- High Call -->
    <div class="section">
      <el-row :gutter="20" class="mb-2">
        <el-col :span="12">
          <label class="form-label">Date of Invitation:</label>
          <el-date-picker
            v-model="highCallDate"
            type="date"
            placeholder="Pick a date"
            style="width:100%"
          />
        </el-col>
        <el-col :span="12">
          <label class="form-label">Their Response:</label>
          <el-select
            v-model="highCallResponse"
            placeholder="Select Response"
            style="width:100%"
          >
            <el-option label="Green" value="Green" />
            <el-option label="Yellow" value="Yellow" />
            <el-option label="Red" value="Red" />
          </el-select>
        </el-col>
        <br><br>
        <el-col :span="24">
          <label class="form-label">Notes:</label>
          <div v-if="highCallResponse === 'Yellow'" class="yellow-note">
            *Please include the timeframe for Yellow response below.
          </div>
          <el-input
            type="textarea"
            v-model="highCallNotes"
            placeholder="Enter any notes here..."
            rows="4"
            class="notes-textarea"
          />
          <p class="col-md-12 note">
            *Notes are public. Please do not share private/confidential information.
          </p>
        </el-col>
      </el-row>
    </div>

    <template slot="footer">
      <p-button @click.native="handleClose">
        Cancel
      </p-button>
      <p-button type="primary" v-if="!saving" @click.native="handleSave">
        Save
      </p-button>
      <p-button type="primary" v-if="saving">
        Saving...
      </p-button>
        &nbsp;&nbsp;
    </template>
  </modal>
</template>

<style scoped>
.top-margin {
  margin-top: 8px;
}
.align-center {
  align-items: center;
}
.section {
  margin-top: 0 !important;
  margin-bottom: 8px;
  padding-top: 0;
}
.divider {
  border: none;
  border-top: 1px solid #eee;
  margin: 24px 0;
}
.note {
  font-size: 1.1em;
  color: #FF2800;
  margin-bottom: 16px;
}
.form-label {
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
}
.mb-2 {
  margin-bottom: 12px;
}
.yellow-note {
  color: #66B1E6;
  font-size: .9em;
  margin-bottom: 6px;
}
.notes-textarea {
  width: 100%;
}
</style>

<script>
import Modal from '../UIComponents/Modal';
import PeopleRepository from '../../repositories/peopleRepository';
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const DisciplesRepository = RepositoryFactory.get('disciples');

function validateDisciple(newDisciplePersonId) {
    return !!newDisciplePersonId;
}

export default {
    name: 'HighCallModal',
    components: {
        Modal
    },
    props: {
        show: Boolean,
        editingData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showModal: false,
            error: false,
            saving: false,

            newDisciplePersonId: null,
            possibleRecipients: [],
            newDisciplerPersonId: null,
            addableDisciplers: [],

            // High Call fields
            highCallDate: new Date(),
            highCallResponse: null,
            highCallNotes: '',

            validationError: {
                disciple: false,
                discipler: false
            }
        };
    },
    watch: {
        show(val) {
            this.showModal = val;
            if (val) {
                this.getAddableDisciplersList();
                // Only load receivers in create mode
                if (!this.editingData && this.newDisciplerPersonId) {
                    this.getPossibleRecipients(this.newDisciplerPersonId);
                }
            }
        },
        editingData: {
            handler(newData) {
                if (newData) {
                    // Populate form with editing data
                    this.highCallDate = newData.highCallDate || new Date();
                    this.highCallResponse = newData.highCallResponse;
                    this.highCallNotes = newData.highCallNotes;

                    // Set presenter but don't allow receiver editing in edit mode
                    this.newDisciplerPersonId = newData.presenterPersonId || null;
                    this.newDisciplePersonId = newData.receiverPersonId || newData.id || null;
                }
            },
            immediate: true
        },
        newDisciplerPersonId(val) {
            // Only load receivers list in create mode
            if (!this.editingData) {
                this.getPossibleRecipients(val);
                this.newDisciplePersonId = null;
            }
        },
        newDisciplePersonId(val) {
            if (!val && !this.editingData) {
                // Only reset fields in create mode when no receiver selected
                this.highCallDate = new Date();
                this.highCallResponse = null;
                this.highCallNotes = '';
            }
        }
    },
    methods: {
        getPresenterName(presenterId) {
            const selectedPresenter = this.addableDisciplers.find((d) => d.id === presenterId);
            return selectedPresenter ? selectedPresenter.name : '';
        },
        handleSave() {
            this.saving = true;

            // Validate presenter/receiver - only validate receiver in create mode
            this.validationError.discipler = !validateDisciple(this.newDisciplerPersonId);
            this.validationError.disciple = !this.editingData && !validateDisciple(this.newDisciplePersonId);

            if (this.validationError.discipler || this.validationError.disciple) {
                this.saving = false;
                return;
            }

            if (this.editingData) {
                // Edit mode - update existing record
                const payload = {
                    presenter: this.getPresenterName(this.newDisciplerPersonId),
                    high_call_presenter_person_id: this.newDisciplerPersonId,
                    high_call_answer: this.highCallResponse,
                    high_call_date: this.highCallDate,
                    high_call_notes: this.highCallNotes
                };

                this.$http.patch(`people/${this.editingData.id}/gospel-highcall`, payload)
                    .then((response) => {
                        this.saving = false;
                        this.$emit('discipleChange');
                        this.$message(response.data.message);
                        this.handleClose();
                    })
                    .catch((error) => {
                        this.saving = false;
                        this.$message.error(error.response.data.message);
                    });
            } else {
                // Create mode - get receiver info from possibleRecipients
                const selectedReceiver = this.possibleRecipients.find((r) => r.id === this.newDisciplePersonId);
                const receiverName = selectedReceiver ? selectedReceiver.name : '';
                const [firstName = '', lastName = ''] = receiverName.split(' ');

                const payload = {
                    first_name: firstName,
                    last_name: lastName,
                    presenter: this.getPresenterName(this.newDisciplerPersonId),
                    high_call_presenter_person_id: this.newDisciplerPersonId,
                    received_high_call: true,
                };

                if (this.highCallResponse) {
                    payload.high_call_answer = this.highCallResponse;
                }
                if (this.highCallDate) {
                    payload.high_call_date = this.highCallDate;
                }
                if (this.highCallNotes) {
                    payload.high_call_notes = this.highCallNotes;
                }

                this.$http.patch(`people/${this.newDisciplePersonId}/gospel-highcall`, payload)
                    .then((response) => {
                        this.saving = false;
                        this.$emit('discipleChange');
                        this.$message(response.data.message);
                        this.handleClose();
                    })
                    .catch((error) => {
                        this.saving = false;
                        this.$message.error(error.response.data.message);
                    });
            }
        },

        handleClose() {
            this.newDisciplePersonId = null;
            this.newDisciplerPersonId = null;

            this.highCallDate = new Date();
            this.highCallResponse = null;
            this.highCallNotes = '';

            this.validationError.disciple = false;
            this.validationError.discipler = false;

            this.$emit('close');
        },
        getAddableDisciplersList() {
            DisciplesRepository.getAddableDisciplersList()
                .then((response) => {
                    this.addableDisciplers = response.data.data;
                })
                .catch((error) => {
                    this.error = error;
                });
        },
        getPossibleRecipients(presenterId) {
            if (!presenterId || !/^\d+$/.test(String(presenterId))) {
                this.possibleRecipients = [];
                return;
            }
            PeopleRepository.getPossibleInvitationRecipientsByPresenter(presenterId, 'high_call')
                .then((response) => {
                    this.possibleRecipients = response.data;
                })
                .catch(() => {
                    this.possibleRecipients = [];
                });
        }
    }
};
</script>
